// DataComponent.tsx
import { useEffect, useRef, useState, useCallback } from 'react';
import { ComboBox, Item, Key } from '@adobe/react-spectrum';
import { useAsyncList } from 'react-stately';
import { DataComponentProps } from './DataComponentProps';
import { useDependency } from '../../../contexts/DependencyProvider';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import { FPAData, FPADataFactory } from '../../../infra/protected/FPA/FPAData';
import { ListFPARequest2 } from '../../../services/soap/project/requests/ListFPARequest2';
import { Col, FilterCol, Order } from '../../../services/soap/project/requests/ListFPARequest';
import { useTranslation } from 'react-i18next';

interface FolderDropDownProps extends DataComponentProps {
    folder_type_id?: number;
    parent_folder_id?: number;
    activity_type_id?: number;
    project_type_id?: number;
    flatView: boolean;
}

interface LoadDataParams {
    searchText?: string;
    id?: number;
    offset?: number;
    limit?: number;
    countOnly?: boolean;
}

const FolderDropDown: React.FC<FolderDropDownProps> = ({ 
  folder_type_id, 
  parent_folder_id,
  activity_type_id,
  project_type_id,
  compKey, 
  label, 
  width, 
  isRequired, 
  onValueChange,
  onBlur,
  value: dvalue,
  flatView,
}) => {
  const { t } = useTranslation();
  const _t = (key: string) => t(`newContextDialog.dataComponents.${key}`, { ns: 'finder' });
  const message = _t('is_required').split('[content]').join(label);
  const [errorMessage, setErrorMessage] = useState(isRequired ? message : '');
  const [initialValueLoaded, setInitialValueLoaded] = useState(false);
  const prevDvalueRef = useRef<string | undefined>(dvalue);
  const { projectService, store } = useDependency();
  const { folderStatusMapping, projectStatusMapping, activityStatusMapping } = usePreloadAssets();
  // const [totalCount, setTotalCount] = useState<number>(0);

  // Clear error when a required default value is present
  useEffect(() => {
    if (isRequired && dvalue && dvalue !== 'undefined') {
      setErrorMessage('');
    }
  }, [isRequired, dvalue]);


  const getTotalCount = useCallback(async (searchText?: string): Promise<number> => {
    const folder_filter: FilterCol[] = [];

    if (folder_type_id) {
      folder_filter.push(new FilterCol('folderType', 'equals', folder_type_id.toString()));
    }
    if(parent_folder_id) {
      folder_filter.push(new FilterCol('parent', 'equals', parent_folder_id.toString()));
    }
    if(activity_type_id){
      folder_filter.push(new FilterCol('relatedToProjectsOfActivityType', 'equals', activity_type_id.toString()));
    }
    if(project_type_id){
      folder_filter.push(new FilterCol('relatedToSlaveProjectType', 'equals', project_type_id.toString()));
    }
    const response = await projectService.listFPA2(
      new ListFPARequest2(
          store.Server, store.SessionId,
          {
              countOnly: true,
              fulltext: searchText ? searchText : undefined,
              flatView: flatView,
              folderFilter:{
                  COL: folder_filter
              },
              ORDER: new Order([
                  new Col('name'), 
                  new Col('created', true)
              ])
          }
      )        
    );
    
    if(response.EXCEPTION) {
      console.error(response.EXCEPTION.message);
      return 0;
    } else {
      return response.count || 0;
    }
  }, [folder_type_id, parent_folder_id, flatView, projectService, store]);

  const loadData = useCallback(async ({ searchText, id, offset = 0, limit = 100 }: LoadDataParams) => {
    const folder_filter: FilterCol[] = [];

    if (folder_type_id) {
      folder_filter.push(new FilterCol('folderType', 'equals', folder_type_id.toString()));
    }
    if(parent_folder_id) {
      folder_filter.push(new FilterCol('parent', 'equals', parent_folder_id.toString()));
    }

    if(activity_type_id){
      folder_filter.push(new FilterCol('relatedToProjectsOfActivityType', 'equals', activity_type_id.toString()));
    }
    if(project_type_id){
      folder_filter.push(new FilterCol('relatedToSlaveProjectType', 'equals', project_type_id.toString()));
    }
    
    if(id){
      folder_filter.push(new FilterCol('id', 'equals', id.toString()));
    }

    const response = await projectService.listFPA2(
      new ListFPARequest2(
          store.Server, store.SessionId,
          {
              offset: offset,
              limit: limit,
              fulltext: searchText ? searchText : undefined,
              flatView: flatView,
              folderFilter:{
                  COL: folder_filter
              },
              ORDER: new Order([
                  new Col('name'), 
                  new Col('created', true)
              ])
          }
      )        
    );
    
    if(response.EXCEPTION) {
      console.error(response.EXCEPTION.message);
      return { items: [], totalCount: 0 };
    } else if(response.ITEMS) {
      const result = response.ITEMS.map((item: any) => FPADataFactory.createFromItem(
                                      item, 
                                      folderStatusMapping, 
                                      projectStatusMapping, 
                                      activityStatusMapping));
      return { items: result, totalCount: response.count || result.length };
    } else {
      return { items: [], totalCount: 0 };
    }
  }, [folder_type_id, parent_folder_id, flatView, projectService, store, folderStatusMapping, projectStatusMapping, activityStatusMapping]);

  const list = useAsyncList<FPAData>({
    async load({ cursor, filterText }) {
      const searchText = filterText || '';
      const cursor_obj = cursor ? JSON.parse(cursor) : null;
      const offset = cursor_obj ? cursor_obj.offset : 0;
      const limit = 50; // Smaller page size for better performance
      
      try {
        let totalCount = 0;
        if(cursor_obj && cursor_obj.total) {
          totalCount = cursor_obj.total || 0;
        } else {
          // First get total count for the current search
          totalCount = await getTotalCount(searchText.length >= 3 ? `${searchText}*` : undefined);
        }
        
        // Then load the actual data with pagination
        const result = await loadData({
          searchText: searchText.length >= 3 ? `${searchText}*` : undefined,
          offset: offset,
          limit: limit
        });

        return {
          items: result.items,
          cursor: offset + limit <= totalCount ? 
          JSON.stringify({offset: offset + limit, total: totalCount}) : undefined
        };
      } catch (error) {
        console.error('Error loading data:', error);
        return {
          items: [],
          cursor: undefined
        };
      }
    },
  });

  let onInputChange = (value: string) => {
    if(value === ''){
      list.setSelectedKeys(new Set([]));
      if(onValueChange)
          onValueChange('', '');
      setErrorMessage('');
    }

    list.setFilterText(value);
  }

  let onSelectionChange = (key: Key | null) => {
    if(key) {
      let itemText = list.getItem(key)?.title;
      list.setSelectedKeys(new Set([key]));
      list.setFilterText(itemText || '');

      if(onValueChange)
          onValueChange(key ? key.toString() : '', itemText || '');
      if(isRequired && !key) 
        setErrorMessage(message);
      else 
        setErrorMessage('');
    } else {
      // Handle clearing selection
      list.setSelectedKeys(new Set([]));
      list.setFilterText('');
      if(onValueChange)
          onValueChange('', '');
      if(isRequired) 
        setErrorMessage(message);
      else 
        setErrorMessage('');
    }
  };
  // Load initial value when component mounts
  useEffect(() => {
    if (dvalue && dvalue !== 'undefined' && !initialValueLoaded) {
      // Load the specific item data
      loadData({ id: parseInt(dvalue) }).then((result) => {
        if (result.items.length > 0) {
          const item = result.items[0];
          // Set the selected key
          list.setSelectedKeys(new Set([dvalue]));
          // Set the filter text to show the item name
          list.setFilterText(item.title);
          // Clear any error since we have a valid default value
          setErrorMessage('');
          // Call the onValueChange callback
          if (onValueChange) {
            onValueChange(dvalue, item.title);
          }
          setInitialValueLoaded(true);
        }
      }).catch((error) => {
        console.error('Error loading initial value:', error);
        setInitialValueLoaded(true);
      });
    }
  }, [dvalue, initialValueLoaded, onValueChange, loadData, list]);

  // Handle value changes after initial load
  useEffect(() => {
    // Only run if the value actually changed and we've loaded the initial value
    if (dvalue !== prevDvalueRef.current && initialValueLoaded) {
      prevDvalueRef.current = dvalue;
      
      if (dvalue && dvalue !== 'undefined') {
        // Check if the item is already in the list
        const existingItem = list.items.find(item => item.id.toString() === dvalue);
        if (existingItem) {
          list.setSelectedKeys(new Set([dvalue]));
          list.setFilterText(existingItem.title);
          setErrorMessage('');
          if (onValueChange) {
            onValueChange(dvalue, existingItem.title);
          }
        } else {
          // Load the item if it's not in the list
          loadData({ id: parseInt(dvalue) }).then((result) => {
            if (result.items.length > 0) {
              const item = result.items[0];
              list.setSelectedKeys(new Set([dvalue]));
              list.setFilterText(item.title);
              setErrorMessage('');
              if (onValueChange) {
                onValueChange(dvalue, item.title);
              }
            }
          });
        }
      } else if (!dvalue || dvalue === 'undefined') {
        // Clear selection if value is empty
        list.setSelectedKeys(new Set([]));
        list.setFilterText('');
        if (onValueChange) {
          onValueChange('', '');
        }
      }
    }
  }, [dvalue, initialValueLoaded, onValueChange, loadData, list]);

  let selectedKeys = list.selectedKeys !== 'all' ? list.selectedKeys.values().next().value : null;
  return (
    <ComboBox
      key={compKey}
      label={label}
      width={width}
      isRequired={isRequired}
      selectedKey={selectedKeys ?? null}
      onSelectionChange={onSelectionChange}
      onBlur={(e) => {
        if (onBlur) onBlur();
      }}
      validationState={errorMessage ? 'invalid' : undefined}
      errorMessage={errorMessage}
      items={list.items}
      inputValue={list.filterText || ''}
      onInputChange={onInputChange}
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
    >
      {(item: FPAData) => (
        <Item key={item.id} textValue={item.title}>
          {item.title}
        </Item>
      )}
    </ComboBox>
  );
};

export default FolderDropDown;
